import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { ButtonGroup } from '@nextui-org/react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import ChatBox from '../chat/ChatBox';
import {
  copyToClientInput,
  exportMessagesPlainText,
  exportMessagesCsv,
  pushUserToClientMessage,
  pushUserToWebsocketMessage,
  selectMessagesWithAssistant,
  selectMessagesWithClient,
  selectAssistantMessageScores,
  setHasAutoReply,
} from '../../store/chat/chatSlice';
import { selectCurrentClient } from '../../store/client/clientSlice';
import { selectSettingsFeatures } from '../../store/settings/settingsSlice';
import { useEffect, useMemo, useRef } from 'react';
import ChatView from '../chat/ChatView';
import {
  deleteChatWithAssistant,
  generateResource,
  generateResponse,
  generateDirections,
  reasonsForLiving,
  generateWrapUp,
  rewriteEmpathetically,
  summariseClientHistory,
  summarisePostClientHistory,
  generateC3PGuide,
  analyseClientHistory_CT_supervision,
  TOAST_NO_MESSAGES,
  generateProblemIdentification,
  generateAttemptedSolutions,
  generateDesiredOutcome,
  generateInterventions,
} from '../../store/chat/chatThunks';
import { FaTrash } from 'react-icons/fa6';
import Icon from '../base/Icon';
import ButtonWithConfirmModal from '../base/ButtonWithConfirmModal';
import FeatureBar from '../feature/FeatureBar';
import FeatureItem from '../feature/FeatureItem';
import ChatInputAssistant from '../chat/input/ChatInputAssistant';
import { PromptType } from '../../types';
import AnimatePresence from '../base/AnimatePresence';
import ButtonExportTxt from '../base/ButtonExportTxt';
import ButtonExportCsv from '../base/ButtonExportCsv';
import { GENERIC_APP } from '../../config/generic';
import { SOS_APP } from '../../config/sos';
import { BASE_APP } from '../../config/base';
import ProposeResponseDropdown from '../feature/ProposeResponseDropdown';
import FeedbackDropdown from '../feature/FeedbackDropdown';

export default function AssistantView() {
  const dispatch = useAppDispatch();
  const features = useAppSelector(selectSettingsFeatures);
  const proposeResponseFeature = features.filter(function (el) {
    return el.key === 'generate_response';
  });

  const currentClient = useAppSelector(selectCurrentClient);
  const messagesWithAssistant = useAppSelector(selectMessagesWithAssistant);
  const messagesWithClient = useAppSelector(selectMessagesWithClient);
  // const [hasRun, setHasRun] = useState('');
  let assistantFunctions = BASE_APP.assistantFunctions;
  if (process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS') {
    assistantFunctions = assistantFunctions.concat(SOS_APP.assistantFunctions);
  } else {
    assistantFunctions = assistantFunctions.concat(
      GENERIC_APP.assistantFunctions
    );
  }

  const assistantName =
    process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC'
      ? GENERIC_APP.assistantName
      : SOS_APP.assistantName;

  const proposeResponseFunction = useMemo(() => {
    const functions = new Map<PromptType, () => void>();

    let functionKey: PromptType = 'generate_response';
    if (assistantFunctions.includes(functionKey)) {
      functions.set(functionKey, () => dispatch(generateResponse()));
    }
    return functions;
  }, [dispatch, assistantFunctions]);

  const featureFunctions = useMemo(() => {
    const functions = new Map<PromptType, () => void>();

    let functionKey: PromptType = 'generate_directions';
    if (assistantFunctions.includes(functionKey)) {
      functions.set(functionKey, () => dispatch(generateDirections()));
    }

    functionKey = 'reasons_for_living';
    if (assistantFunctions.includes(functionKey)) {
      functions.set(functionKey, () => dispatch(reasonsForLiving()));
    }

    functionKey = 'generate_C3P_guide';
    if (assistantFunctions.includes(functionKey)) {
      functions.set(functionKey, () => dispatch(generateC3PGuide()));
    }

    functionKey = 'generate_wrap_up';
    if (assistantFunctions.includes(functionKey)) {
      functions.set(functionKey, () => dispatch(generateWrapUp()));
    }

    functionKey = 'recommend_resource';
    if (assistantFunctions.includes(functionKey)) {
      functions.set(functionKey, () => dispatch(generateResource()));
    }

    functionKey = 'summarize';
    if (assistantFunctions.includes(functionKey)) {
      functions.set(functionKey, () => dispatch(summariseClientHistory()));
    }

    functionKey = 'summarize_post';
    if (assistantFunctions.includes(functionKey)) {
      functions.set(functionKey, () => dispatch(summarisePostClientHistory()));
    }

    functionKey = 'empathetic_rewrite';
    if (assistantFunctions.includes(functionKey)) {
      functions.set(functionKey, () => dispatch(rewriteEmpathetically()));
    }

    if (process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS') {
      functionKey = 'generate_feedback_CT_supervision';
      if (assistantFunctions.includes(functionKey)) {
        functions.set(functionKey, () =>
          dispatch(analyseClientHistory_CT_supervision())
        );
      }
    }

    return functions;
  }, [dispatch, assistantFunctions]);

  const featureFunctionsRef = useRef<any>();
  useEffect(() => {
    featureFunctionsRef.current = featureFunctions;
  }, [featureFunctions]);

  // handles auto-run functionality for features
  useEffect(() => {
    if (!messagesWithClient) return;

    const n = messagesWithClient.length;
    if (n === 0) return;

    if (messagesWithClient[n - 1].role === 'user') return;

    if (messagesWithClient[n - 1].content === '') return;

    if (messagesWithClient[n - 1].hasAutoReply) return;

    if (featureFunctions !== featureFunctionsRef.current) {
      // prevents autorun from triggering twice due to this state changing
      return;
    }

    features.forEach((feat) => {
      if (feat.isEnabled && feat.isAutoRun) {
        dispatch(
          setHasAutoReply({
            messageId: messagesWithClient[n - 1].messageId,
            userId: currentClient?.userId,
          })
        );

        switch (feat.key) {
          case 'generate_response':
            return dispatch(generateResponse());
          case 'generate_problem_identification':
            return dispatch(generateProblemIdentification());
          case 'generate_attempted_solutions':
            return dispatch(generateAttemptedSolutions());
          case 'generate_desired_outcome':
            return dispatch(generateDesiredOutcome());
          case 'generate_interventions':
            return dispatch(generateInterventions());
          case 'generate_C3P_guide':
            return dispatch(generateC3PGuide());

          default:
            return;
        }
      }
    });
  }, [dispatch, messagesWithClient, features, featureFunctions, currentClient]);

  const handleTransfer = (message: string, sendImmediately = false) => {
    if (!currentClient) {
      return;
    }
    const { userId, userType } = currentClient;
    if (sendImmediately) {
      dispatch(
        pushUserToClientMessage({
          userId,
          messageId: uuidv4(),
          payload: message,
        })
      );
      switch (userType) {
        case 'client':
          dispatch(pushUserToWebsocketMessage({ userId, payload: message }));
          break;
      }
    } else {
      dispatch(copyToClientInput({ userId, payload: message }));
    }
  };

  const handleClear = () => {
    if (currentClient?.userId) {
      dispatch(deleteChatWithAssistant(currentClient.userId));
    }
  };

  const clientName = useMemo(() => {
    if (currentClient?.userType === 'actor') {
      return currentClient.name || currentClient.username;
    }

    return (
      currentClient?.friendlyName ||
      currentClient?.username ||
      currentClient?.userId
    );
  }, [currentClient]);

  const scores = useAppSelector(selectAssistantMessageScores);

  const handleExportPlainText = () => {
    // TODO: include message quoting format
    if (messagesWithAssistant.length === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }

    if (messagesWithAssistant) {
      exportMessagesPlainText(
        messagesWithAssistant,
        {
          messagesLabel: `${clientName} [${currentClient?.userId}] assistant`,
          assistantLabel: assistantName,
          userLabel: `RESPONDER`,
          dialogue: 'assistant',
        },
        scores,
        {}
      );
    }
  };

  const handleExportCsv = () => {
    if (messagesWithAssistant.length === 0) {
      toast.warn(TOAST_NO_MESSAGES);
      return;
    }

    if (messagesWithAssistant) {
      exportMessagesCsv(
        messagesWithAssistant,
        {
          messagesLabel: `${clientName} [${currentClient?.userId}] assistant`,
          assistantLabel: assistantName,
          userLabel: `RESPONDER`,
          dialogue: 'assistant',
        },
        scores,
        {}
      );
    }
  };

  const hasFeaturesEnabled = useMemo(
    () => features.reduce((prev, curr) => prev || curr.isEnabled, false),
    [features]
  );

  const featuresBar = hasFeaturesEnabled && !currentClient?.isArchived && (
    <FeatureBar>
      {proposeResponseFeature[0].isEnabled &&
        process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC' && (
          <div>
            <FeatureItem
              key={proposeResponseFeature[0].key}
              feature={proposeResponseFeature[0]}
              onPress={proposeResponseFunction.get(
                proposeResponseFeature[0].key
              )}
            />
          </div>
        )}
      {proposeResponseFeature[0].isEnabled &&
        process.env.REACT_APP_DEPLOYMENT_TYPE === 'SOS' && (
          <div className="propose-response-group">
            <FeatureItem
              key={proposeResponseFeature[0].key}
              feature={proposeResponseFeature[0]}
              onPress={proposeResponseFunction.get(
                proposeResponseFeature[0].key
              )}
            />
            <ProposeResponseDropdown />
          </div>
        )}
      {features
        .filter((feat) => featureFunctions.has(feat.key))
        .map(
          (feat) =>
            feat.isEnabled && (
              <FeatureItem
                key={feat.key}
                feature={feat}
                onPress={featureFunctions.get(feat.key)}
              />
            )
        )}
      {process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC' && (
        <div className="feedback-group">
          <FeedbackDropdown />
        </div>
      )}
    </FeatureBar>
  );

  const buttonExportTxt = <ButtonExportTxt onPress={handleExportPlainText} />;

  const buttonExportCsv = <ButtonExportCsv onPress={handleExportCsv} />;

  const buttonClear = (
    <ButtonWithConfirmModal
      onPress={handleClear}
      buttonContent={<Icon icon={FaTrash} />}
      buttonProps={{
        color: 'danger',
        isDisabled: !messagesWithAssistant?.length,
      }}
      tooltipProps={{
        content: 'Clear conversation',
        placement: 'bottom',
        color: 'danger',
      }}
      confirmLabel="Clear"
      modalHeader="Clear conversation?"
    />
  );

  const chatBox = (
    <AnimatePresence mode="wait">
      {currentClient && (
        <ChatBox
          key={currentClient.userId}
          isAssistant
          messages={messagesWithAssistant}
          onTransfer={handleTransfer}
          headerTitle={assistantName}
          headerElements={[
            <ButtonGroup
              key="chat-actions"
              isIconOnly
              variant="light"
              className="assistant-button-group"
            >
              {buttonExportTxt}
              {buttonExportCsv}
              {buttonClear}
            </ButtonGroup>,
          ]}
          inputField={
            <div className="relative flex flex-col items-start gap-2">
              {featuresBar}
              <ChatInputAssistant />
            </div>
          }
        />
      )}
    </AnimatePresence>
  );

  return (
    <ChatView
      chatId={currentClient?.userId}
      chatBox={chatBox}
      chatType="assistant"
    />
  );
}
